/**
 * One rotating hero that carries the promotional weight of the home page.
 *
 * This replaced two adjacent sections — a membership conversion panel and a
 * community statement card, each with its own separate rotation. Read together
 * they segmented one idea into two boxes and rotated independently, so the page
 * never settled. A single frame promoting a different corner of the site per
 * slide says the same things in one place.
 *
 * Every slide is a photograph with type over it, so two problems are handled
 * deliberately rather than left to chance:
 *
 *   - Legibility never depends on the image. A fixed scrim sits between the photo
 *     and the type, so a bright frame cannot wash the headline out.
 *   - The set spans 0.75 to 2.50 aspect ratio. `cover` in a 16/9 frame would take
 *     the crop out of the middle of the portrait-orientation frame and slice the
 *     faces, so those slides are fitted whole against a blurred copy of
 *     themselves instead of being cropped.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "wouter";
import gsap from "gsap";
import { ArrowRight, CalendarDays, Sparkles, UserPlus, Users } from "lucide-react";
import PromoHeroMotif, { type MotifVariant } from "@/components/PromoHeroMotif";
import LordIcon from "@/components/LordIcon";
import ResponsiveImage from "@/components/ResponsiveImage";
import FeaturedEventAction from "@/components/FeaturedEventAction";
import type { CHIEvent } from "@/lib/eventsData";

type Slide = {
  key: string;
  eyebrow: string;
  headline: string;
  accent: string;
  body: string;
  image: string;
  /** object-position for a cropped slide, biased away from faces. */
  focal?: string;
  /**
   * Fit the whole frame instead of cropping it.
   *
   * For images whose ratio is far from the frame's: cropping a 1440x1920 portrait
   * into 16/9 discards well over half of it, through the middle, which is where
   * the people are.
   */
  contain?: boolean;
  cta: { label: string; href: string; icon: typeof ArrowRight };
  secondary?: { label: string; href: string };
  /**
   * The drawn motif for this slide, where it has one.
   *
   * Optional: community, events and the speaker series carry no drawn figure, so
   * their copy starts at the eyebrow. Where a motif is set it is also the
   * fallback for a Lordicon that fails to load, since a slot rendering nothing
   * is worse than one rendering the drawn figure.
   */
  motif?: MotifVariant;
  /**
   * Path to this slide's Lordicon `.lottie`, when the asset exists.
   *
   * These are licensed downloads, so they are named here rather than fetched:
   * drop the file at the path below and the slide switches from the drawn motif
   * to the animated icon with no further change.
   *
   * Wanted, and still missing:
   *   membership → /media/icons/membership-profile.lottie
   *   community  → /media/icons/community-people.lottie
   *   events     → /media/icons/events-calendar.lottie
   *   mission    → /media/icons/mission-compass.lottie
   */
  lordicon?: string;
};

const SLIDES: Slide[] = [
  {
    key: "membership",
    motif: "connection",
    eyebrow: "Membership",
    headline: "Find your next",
    accent: "connection.",
    body: "A profile opens the door to people, ideas, and momentum.",
    image: "/media/backgrounds/community.webp",
    focal: "50% 32%",
    cta: { label: "Join free", href: "/create-account?source=home-membership", icon: UserPlus },
    secondary: { label: "Explore benefits", href: "/membership" },
  },
  {
    key: "community",
    eyebrow: "The community",
    headline: "Where bold conversations",
    accent: "become opportunities.",
    body: "Join a community of professionals who believe human connection is the ultimate competitive advantage.",
    image: "/media/backgrounds/community-networking.webp",
    focal: "50% 40%",
    cta: { label: "Explore the community", href: "/community", icon: Users },
  },
  {
    key: "events",
    eyebrow: "Events",
    headline: "A calendar",
    accent: "worth joining.",
    body: "Where bold thinkers, innovators, and leaders come together to grow and thrive.",
    // 1440x1920 portrait: fitted whole rather than cropped through the faces.
    image: "/media/events/professional-panel.webp",
    contain: true,
    cta: { label: "View upcoming events", href: "/calendar-of-events", icon: CalendarDays },
  },
  {
    key: "speakers",
    lordicon: "/media/icons/speaker-microphone.lottie",
    eyebrow: "Speaker series",
    headline: "Visionary leaders,",
    accent: "practical perspectives.",
    body: "The most valuable opportunities come through shared wisdom and human connection.",
    image: "/media/backgrounds/group-headshots.webp",
    focal: "50% 28%",
    cta: { label: "Meet the speakers", href: "/featured-speakers", icon: Sparkles },
  },
  {
    key: "mission",
    motif: "lattice",
    eyebrow: "Mission & values",
    headline: "A movement dedicated to",
    accent: "elevating each other.",
    body: "Authentic relationships are the foundation of every breakthrough.",
    image: "/media/backgrounds/faces-mosaic.webp",
    focal: "50% 34%",
    cta: { label: "Our mission & values", href: "/mission-and-values", icon: ArrowRight },
  },
];

const DWELL_MS = 6200;

export default function HomePromoHero({ featuredEvent }: { featuredEvent?: CHIEvent }) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const frameRef = useRef<HTMLDivElement>(null);
  const previousRef = useRef(0);
  /*
   * Icons that could not be loaded, so the slide shows its drawn motif instead.
   * Held here rather than per-slide so a missing file is recorded once.
   */
  const [unavailableIcons, setUnavailableIcons] = useState<string[]>([]);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReducedMotion(media.matches);
    sync();
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, []);

  const go = useCallback((next: number) => {
    setActive(current => {
      previousRef.current = current;
      return (next + SLIDES.length) % SLIDES.length;
    });
  }, []);

  useEffect(() => {
    if (reducedMotion || paused) return;
    const timer = window.setInterval(() => go(active + 1), DWELL_MS);
    return () => window.clearInterval(timer);
  }, [active, go, paused, reducedMotion]);

  /*
   * The outgoing slide is faded out rather than hidden, so the frame is never
   * momentarily empty between two photographs.
   */
  useEffect(() => {
    const frame = frameRef.current;
    if (!frame || reducedMotion) return;
    const panes = frame.querySelectorAll<HTMLElement>(".promo-hero__pane");
    panes.forEach((pane, index) => {
      const isActive = index === active;
      gsap.to(pane, {
        autoAlpha: isActive ? 1 : 0,
        duration: 0.85,
        ease: "sine.inOut",
        overwrite: "auto",
      });
      if (isActive) {
        gsap.fromTo(
          pane.querySelector(".promo-hero__copy"),
          { autoAlpha: 0, yPercent: -50, y: 18 },
          { autoAlpha: 1, yPercent: -50, y: 0, duration: 0.7, ease: "power3.out", delay: 0.18, overwrite: "auto" },
        );
      }
    });
  }, [active, reducedMotion]);

  return (
    <section
      className="promo-hero"
      aria-roledescription="carousel"
      aria-label="What The CHI Alliance offers"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
      onKeyDown={event => {
        if (event.key === "ArrowRight") { event.preventDefault(); go(active + 1); }
        if (event.key === "ArrowLeft") { event.preventDefault(); go(active - 1); }
      }}
    >
      <div className="container">
        {featuredEvent && (
          <div className="promo-hero__featured-event">
            <div>
              <span>Featured event</span>
              <strong>{featuredEvent.title}</strong>
            </div>
            <FeaturedEventAction event={featuredEvent} />
          </div>
        )}
        <div className="promo-hero__frame" ref={frameRef}>
          {SLIDES.map((slide, index) => {
            const Icon = slide.cta.icon;
            const isActive = index === active;
            return (
              <article
                className={`promo-hero__pane${isActive ? " is-active" : ""}`}
                key={slide.key}
                aria-hidden={!isActive}
                aria-roledescription="slide"
                aria-label={`${index + 1} of ${SLIDES.length}: ${slide.eyebrow}`}
              >
                {/* A blurred, cropped copy of the same photograph fills the frame
                    behind a whole-image slide, so nothing is lost and no letterbox
                    bar appears. */}
                {slide.contain && (
                  <ResponsiveImage className="promo-hero__fill" src={slide.image} alt="" aria-hidden="true" loading="lazy" decoding="async" maxWidth={1600} sizes="100vw" />
                )}
                <ResponsiveImage
                  className={`promo-hero__photo${slide.contain ? " promo-hero__photo--contain" : ""}`}
                  src={slide.image}
                  alt=""
                  aria-hidden="true"
                  style={slide.focal ? { objectPosition: slide.focal } : undefined}
                  loading={index === 0 ? "eager" : "lazy"}
                  fetchPriority={index === 0 ? "high" : "low"}
                  decoding="async"
                  maxWidth={1600}
                  sizes="100vw"
                />
                <div className="promo-hero__scrim" aria-hidden="true" />

                <div className="promo-hero__copy">
                  {(() => {
                    const iconSrc = slide.lordicon;
                    const iconUsable = Boolean(iconSrc) && !unavailableIcons.includes(iconSrc as string);
                    // No icon and no motif: render no slot, rather than an empty
                    // box holding a margin above the eyebrow.
                    if (!iconUsable && !slide.motif) return null;
                    return (
                  <div className="promo-hero__motif-slot" aria-hidden="true">
                    {iconUsable ? (
                      <span className="promo-hero__lordicon">
                        {/*
                          Rendered exactly as Lordicon exported it: no `colors`
                          override and no tint layer over the top. Forcing the mark
                          white and painting a gradient through it with a blend mode
                          was a workaround for icons that were not gradient to begin
                          with; a gradient-style icon already carries its own, and
                          the blend layer only muddied it.
                        */}
                        <LordIcon
                          src={iconSrc as string}
                          trigger="loop"
                          onUnavailable={() => setUnavailableIcons(current => (
                            current.includes(iconSrc as string) ? current : [...current, iconSrc as string]
                          ))}
                        />
                      </span>
                    ) : (
                      <PromoHeroMotif variant={slide.motif as MotifVariant} active={isActive} />
                    )}
                  </div>
                    );
                  })()}
                  <p className="promo-hero__eyebrow">{slide.eyebrow}</p>
                  <h2 className="promo-hero__headline">
                    {slide.headline} <span>{slide.accent}</span>
                  </h2>
                  <p className="promo-hero__body">{slide.body}</p>
                  <div className="promo-hero__actions">
                    <div className="chi-cta-pair">
                      <Link className="promo-hero__cta" href={slide.cta.href} tabIndex={isActive ? undefined : -1}>
                        {slide.cta.label} <Icon size={17} aria-hidden="true" />
                      </Link>
                      {slide.secondary && (
                        <Link className="promo-hero__cta promo-hero__cta--ghost" href={slide.secondary.href} tabIndex={isActive ? undefined : -1}>
                          {slide.secondary.label} <ArrowRight size={15} aria-hidden="true" />
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </article>
            );
          })}

          <button
            className="promo-hero__arrow promo-hero__arrow--prev"
            type="button"
            onClick={() => go(active - 1)}
            aria-label="Show previous slide"
          >
            <ArrowRight size={18} aria-hidden="true" />
          </button>
          <button
            className="promo-hero__arrow promo-hero__arrow--next"
            type="button"
            onClick={() => go(active + 1)}
            aria-label="Show next slide"
          >
            <ArrowRight size={18} aria-hidden="true" />
          </button>

          <div className="promo-hero__dots" role="tablist" aria-label="Choose a slide">
            {SLIDES.map((slide, index) => (
              <button
                key={slide.key}
                type="button"
                role="tab"
                aria-selected={index === active}
                aria-label={`Show ${slide.eyebrow}`}
                className={index === active ? "is-active" : ""}
                onClick={() => go(index)}
              />
            ))}
          </div>
        </div>
      </div>

      <style>{`
        .promo-hero{background:#0D1220;padding:clamp(3.5rem,6vw,6rem) 0}
        .promo-hero__featured-event{display:flex;align-items:center;justify-content:space-between;gap:1rem;margin:0 0 1rem;padding:1rem 1.2rem;border:1px solid rgba(233,30,140,.28);border-radius:1rem;background:linear-gradient(100deg,rgba(233,30,140,.12),rgba(245,166,35,.06));color:#fff}
        .promo-hero__featured-event div{display:flex;flex-direction:column;gap:.25rem;min-width:0}
        .promo-hero__featured-event span{color:#F9A8D4;font:700 .65rem 'Space Grotesk',sans-serif;letter-spacing:.12em;text-transform:uppercase}
        .promo-hero__featured-event strong{font:600 clamp(1rem,2vw,1.25rem) 'Space Grotesk',sans-serif}
        .promo-hero__featured-event .featured-event__action{white-space:nowrap}
        .promo-hero__frame{position:relative;aspect-ratio:16/9;min-height:26rem;overflow:hidden;border:1px solid rgba(255,255,255,.09);border-radius:1.5rem;background:linear-gradient(135deg,#1a0a2e,#0d1a2e 50%,#0a1a1a);box-shadow:0 28px 70px rgba(0,0,0,.34)}
        .promo-hero__pane{position:absolute;inset:0;visibility:hidden;opacity:0}
        .promo-hero__pane.is-active{visibility:visible;opacity:1}
        .promo-hero__fill{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;filter:blur(34px) saturate(1.25);transform:scale(1.12);opacity:.55}
        .promo-hero__photo{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;object-position:center}
        .promo-hero__photo--contain{object-fit:contain;object-position:center right}
        .promo-hero__scrim{position:absolute;inset:0;background:linear-gradient(96deg,rgba(6,10,22,.95) 0%,rgba(6,10,22,.86) 38%,rgba(6,10,22,.42) 66%,rgba(6,10,22,.16) 100%),linear-gradient(180deg,rgba(6,10,22,.32),transparent 42%,rgba(6,10,22,.58))}
        .promo-hero__copy{position:absolute;z-index:2;top:50%;left:clamp(1.5rem,4.5vw,4rem);box-sizing:border-box;width:min(100%,36rem);padding:40px;transform:translateY(-50%)}
        .promo-hero__eyebrow{display:table;margin:0 0 1rem;padding:.34rem .62rem;border:1px solid rgba(233,30,140,.42);border-radius:999px;color:#F9A8D4;background:rgba(233,30,140,.13);font-family:'Space Grotesk',sans-serif;font-size:.68rem;font-weight:800;letter-spacing:.09em;text-transform:uppercase}
        .promo-hero__headline{margin:0;color:#fff;font-family:'Bebas Neue',sans-serif;font-size:clamp(2.1rem,4.6vw,3.9rem);font-weight:800;letter-spacing:.02em;line-height:1.02;text-shadow:0 3px 18px rgba(0,0,0,.6)}
        .promo-hero__headline span{background:linear-gradient(135deg,#00D4FF,#E91E8C);-webkit-background-clip:text;background-clip:text;-webkit-text-fill-color:transparent}
        .promo-hero__body{max-width:29rem;margin:1.1rem 0 1.9rem;color:#E2E8F0;font-family:'Inter',sans-serif;font-size:var(--chi-body-compact);line-height:1.65;text-shadow:0 2px 12px rgba(0,0,0,.7)}
        .promo-hero__actions{position:relative}
        /* Sits in the copy stack above the eyebrow, on the same left edge as the
           type. No frame around it: the motif is white over a scrimmed photograph,
           and a ring only competed with the headline beneath it. A drop shadow
           does the separating instead. */
        .promo-hero__motif-slot{position:relative;width:clamp(6.9rem,9.5vw,8.75rem);margin:0 0 clamp(.9rem,1.8vw,1.4rem);color:#FFFFFF;filter:drop-shadow(0 4px 14px rgba(0,0,0,.55));pointer-events:none}
        .promo-hero__motif{position:relative;z-index:1;display:block;width:100%;height:auto;overflow:visible}
        /* Transparent throughout: the icon sits directly on the photograph. */
        .promo-hero__lordicon{position:relative;display:block;width:100%;aspect-ratio:1;background:none}
        .promo-hero__lordicon lord-icon{display:block;width:100%;height:100%;background:none}
        .promo-hero__cta{display:inline-flex;align-items:center;gap:.5rem;min-height:2.95rem;padding:.7rem 1.5rem;border:0;border-radius:.7rem;color:#fff;background:linear-gradient(135deg,#E91E8C,#C2185B);box-shadow:0 12px 28px rgba(233,30,140,.28);font-family:'Space Grotesk',sans-serif;font-size:.9rem;font-weight:750;text-decoration:none;transition:transform .18s,box-shadow .18s}
        .promo-hero__cta:hover,.promo-hero__cta:focus-visible{outline:0;transform:translateY(-2px);box-shadow:0 16px 34px rgba(233,30,140,.38)}
        .promo-hero__cta--ghost{border:1px solid rgba(255,255,255,.22);color:#E2E8F0;background:rgba(8,12,24,.55);box-shadow:none}
        .promo-hero__cta--ghost:hover,.promo-hero__cta--ghost:focus-visible{border-color:rgba(244,114,182,.6);background:rgba(8,12,24,.78);color:#fff;box-shadow:none}
        .promo-hero__arrow{position:absolute;z-index:3;top:50%;display:grid;width:2.9rem;height:2.9rem;place-items:center;border:1px solid rgba(255,255,255,.2);border-radius:50%;color:#fff;background:rgba(6,10,22,.62);cursor:pointer;transform:translateY(-50%);transition:background .18s,border-color .18s}
        .promo-hero__arrow:hover,.promo-hero__arrow:focus-visible{outline:0;border-color:rgba(244,114,182,.65);background:rgba(6,10,22,.9)}
        .promo-hero__arrow--prev{left:clamp(.6rem,1.6vw,1.2rem)}
        .promo-hero__arrow--prev svg{transform:rotate(180deg)}
        .promo-hero__arrow--next{right:clamp(.6rem,1.6vw,1.2rem)}
        .promo-hero__dots{position:absolute;z-index:3;right:0;bottom:clamp(1rem,2.4vw,1.75rem);left:0;display:flex;justify-content:center;gap:.5rem}
        .promo-hero__dots button{width:.6rem;height:.6rem;padding:0;border:0;border-radius:999px;background:rgba(255,255,255,.32);cursor:pointer;transition:width .22s,background .22s}
        .promo-hero__dots button.is-active{width:1.75rem;background:linear-gradient(90deg,#F5A623,#E91E8C)}
        .promo-hero__dots button:focus-visible{outline:2px solid #fff;outline-offset:3px}
        @media(max-width:900px){
          .promo-hero__frame{aspect-ratio:4/5;min-height:32rem}
          .promo-hero__scrim{background:linear-gradient(180deg,rgba(6,10,22,.42) 0%,rgba(6,10,22,.6) 38%,rgba(6,10,22,.95) 82%)}
          .promo-hero__copy{top:auto;bottom:clamp(3.4rem,9vw,4.6rem);left:clamp(1.1rem,4vw,1.75rem);width:auto;right:clamp(1.1rem,4vw,1.75rem);transform:none}
          .promo-hero__photo--contain{object-position:center top}
          .promo-hero__arrow{display:none}
          .promo-hero__copy{padding:24px}
          .promo-hero__motif-slot{width:clamp(5.75rem,17.5vw,7.25rem);margin-bottom:.8rem}
        }
        @media(max-width:560px){.promo-hero__featured-event{align-items:stretch;flex-direction:column}.promo-hero__featured-event .featured-event__action{width:100%}}
        @media(prefers-reduced-motion:reduce){.promo-hero__featured-event *{transition:none!important}}
      `}</style>
    </section>
  );
}
